import { useCallback } from "react";
import { push } from "connected-react-router";
import { useAppDispatch } from "store/hooks";
import classNames from "classnames";
import { Card } from "@remo-co/ui-core/src/components/Card";
import { Typography } from "@remo-co/ui-core/src/components/Typography";
import { ArrowRightAlt } from "@remo-co/ui-core/src/icons/ArrowRightAlt";
import { useI18n } from "i18n";
import useEnterLeaveEvent from "modules/event/hooks/useEnterLeaveEvent";
import { Actions, trackAction } from "modules/monitoring";
import { IEvent } from "modules/event/types";
import { DNS_KEYS } from "modules/globalUserPreference";
import { Lock as LockIcon } from "@remo-co/ui-core/src/icons/Lock";

import { useSelector } from "react-redux";
import { addDialogNotification } from "modules/dialogNotification/redux/dialogNotificationSlice";
import { selectCurrentUserPreferences } from "modules/userPreferences/redux/selectors";
import { useStyles } from "./MenuItem.styles";

interface IMenuItemProps {
  event: IEvent;
  currentEventId: string | null;
  preview?: boolean;
  presentationMode?: boolean;
  menuDialogStyle?: string;
}

const { disableSwitchEventNotification } = DNS_KEYS;

const MenuItem = ({
  event,
  currentEventId,
  preview,
  presentationMode,
  menuDialogStyle,
}: IMenuItemProps) => {
  const dispatch = useAppDispatch();
  const currentUserPreferences = useSelector(selectCurrentUserPreferences);
  const { t } = useI18n(["common", "eventDirectory"]);
  const styles = useStyles();
  const { leaveEvent } = useEnterLeaveEvent(currentEventId);

  const isDNSDisabled = useCallback(
    () =>
      !(
        currentUserPreferences &&
        currentUserPreferences[disableSwitchEventNotification]
      ),
    [currentUserPreferences],
  );

  const navigateToEvent = () => {
    leaveEvent();

    trackAction(Actions.DIRECTORY_EVENT_CLICK, { event });
    dispatch(push(`/e/${event.code}?autojoin=1`));
  };

  const onEventClick = () => {
    if (isDNSDisabled()) {
      dispatch(
        addDialogNotification({
          title: t("common:are.you.sure"),
          message: t("eventDirectory:go.to.event.confirm", {
            name: event.name,
          }),
          hasDoNotShowAgain: true,
          isDoNotShowGlobal: true,
          dnsKey: disableSwitchEventNotification,
          hideCloseButton: true,
          confirmText: t("eventDirectory:go.to.event"),
          confirmTestId: "confirm-go-to-event",
          onConfirm: navigateToEvent,
          dismissText: t("eventDirectory:button.cancel"),
          className: menuDialogStyle,
        }),
      );
    } else {
      navigateToEvent();
    }
  };

  return (
    <Card
      size="xs"
      flex
      fullWidth
      className={classNames(styles.cardItems, {
        [styles.cardItemsPresentationMode]: presentationMode,
      })}
      justifyContent="flex-start"
      onClick={!preview ? onEventClick : undefined}
      data-testid="menu-item-card"
    >
      <img
        src={event.logoURL}
        alt={event.name}
        className={classNames(styles.eventImage, {
          [styles.eventImagePresentationMode]: presentationMode,
        })}
      />
      <Typography
        variant="h5"
        color="textPrimary"
        className={classNames({
          [styles.itemTextPresentationMode]: presentationMode,
        })}
      >
        {event.name}
      </Typography>
      {event?.isPrivate && (
        <span
          className={classNames(styles.lockIcon, {
            [styles.itemTextPresentationMode]: presentationMode,
          })}
        >
          <LockIcon />
        </span>
      )}
      <Typography
        variant="subtitle1"
        color="textPrimary"
        className={classNames(styles.enterLabel, {
          [styles.itemTextPresentationMode]: presentationMode,
          [styles.isPrivateEvent]: event?.isPrivate,
        })}
      >
        {t("eventDirectory:enter")} <ArrowRightAlt />
      </Typography>
    </Card>
  );
};

export default MenuItem;
